const LIGHT_CAPABILITY_BASE_IDS = [
  'light_temperature',
  'light_hue',
  'light_saturation',
  'light_mode',
  'dim',
  'onoff',
] as const;

type LightCapabilityBaseId = (typeof LIGHT_CAPABILITY_BASE_IDS)[number];

interface DeviceLightCapabilityDevice {
  capabilitiesObj?: Record<string, DeviceLightCapability | undefined> | null;
  ui?: {
    quickAction?: string | null;
    state?: string | null;
  } | null;
}

interface DeviceLightCapability {
  getable?: boolean;
  type?: string;
}

interface DeviceLightCapabilityResolutionProps {
  device: DeviceLightCapabilityDevice | null | undefined;
  preferredCapabilityId?: string | null;
}

export type DeviceLightCapabilityIds = Record<LightCapabilityBaseId, string | null>;

export interface DeviceLightCapabilityValues {
  dim: number | null | undefined;
  light_hue: number | null | undefined;
  light_mode: string | null | undefined;
  light_saturation: number | null | undefined;
  light_temperature: number | null | undefined;
  onoff: boolean | null | undefined;
}

interface DeviceLightCapabilityValueProps {
  capabilityIds: DeviceLightCapabilityIds;
  values: Record<string, unknown>;
}

export function getDeviceLightCapabilityIds(
  props: DeviceLightCapabilityResolutionProps,
): DeviceLightCapabilityIds {
  const onOffCapabilityId = getOnOffCapabilityId(props);
  const referenceCapabilityId = onOffCapabilityId ?? getLightReferenceCapabilityId(props) ?? null;
  const instanceSuffix = getCapabilityInstanceSuffix(referenceCapabilityId);

  // The selected state or action capability identifies the physical output. Match its suffix for
  // every related light value, while allowing shared unsuffixed capabilities as fallbacks.
  return {
    light_temperature: getMatchingCapabilityId({
      baseId: 'light_temperature',
      device: props.device,
      instanceSuffix,
    }),
    light_hue: getMatchingCapabilityId({
      baseId: 'light_hue',
      device: props.device,
      instanceSuffix,
    }),
    light_saturation: getMatchingCapabilityId({
      baseId: 'light_saturation',
      device: props.device,
      instanceSuffix,
    }),
    light_mode: getMatchingCapabilityId({
      baseId: 'light_mode',
      device: props.device,
      instanceSuffix,
    }),
    dim: getMatchingCapabilityId({
      baseId: 'dim',
      device: props.device,
      instanceSuffix,
    }),
    onoff: onOffCapabilityId,
  };
}

export function getDeviceLightCapabilityIdList(capabilityIds: DeviceLightCapabilityIds): string[] {
  const resolvedCapabilityIds: string[] = [];

  for (const baseId of LIGHT_CAPABILITY_BASE_IDS) {
    const capabilityId = capabilityIds[baseId];

    if (capabilityId != null) {
      resolvedCapabilityIds.push(capabilityId);
    }
  }

  return resolvedCapabilityIds;
}

export function getDeviceLightCapabilityValues(
  props: DeviceLightCapabilityValueProps,
): DeviceLightCapabilityValues {
  return {
    light_temperature: getNullableNumberValue(props, 'light_temperature'),
    light_hue: getNullableNumberValue(props, 'light_hue'),
    light_saturation: getNullableNumberValue(props, 'light_saturation'),
    light_mode: getNullableStringValue(props, 'light_mode'),
    dim: getNullableNumberValue(props, 'dim'),
    onoff: getNullableBooleanValue(props, 'onoff'),
  };
}

function getOnOffCapabilityId(props: DeviceLightCapabilityResolutionProps): string | null {
  const candidateIds = [
    props.preferredCapabilityId,
    props.device?.ui?.state,
    props.device?.ui?.quickAction,
    'onoff',
  ];

  for (const capabilityId of candidateIds) {
    if (getCapabilityBaseId(capabilityId) !== 'onoff') {
      continue;
    }

    if (isReadableBooleanCapability(props.device, capabilityId) === true) {
      return capabilityId;
    }
  }

  for (const capabilityId of Object.keys(props.device?.capabilitiesObj ?? {})) {
    const isOnOffCapability = getCapabilityBaseId(capabilityId) === 'onoff';

    if (
      isOnOffCapability === true &&
      isReadableBooleanCapability(props.device, capabilityId) === true
    ) {
      return capabilityId;
    }
  }

  return null;
}

function getLightReferenceCapabilityId(props: DeviceLightCapabilityResolutionProps): string | null {
  const candidateIds = [
    props.preferredCapabilityId,
    props.device?.ui?.state,
    props.device?.ui?.quickAction,
  ];

  for (const capabilityId of candidateIds) {
    const baseId = getCapabilityBaseId(capabilityId);

    if (isLightCapabilityBaseId(baseId) !== true) {
      continue;
    }

    if (hasCapability(props.device, capabilityId) === true) {
      return capabilityId;
    }
  }

  return findCapabilityIdByBaseId(props.device, 'dim');
}

function getMatchingCapabilityId(props: {
  baseId: LightCapabilityBaseId;
  device: DeviceLightCapabilityDevice | null | undefined;
  instanceSuffix: string | null;
}): string | null {
  if (props.instanceSuffix != null) {
    const instanceCapabilityId = `${props.baseId}.${props.instanceSuffix}`;

    if (hasCapability(props.device, instanceCapabilityId) === true) {
      return instanceCapabilityId;
    }
  }

  if (hasCapability(props.device, props.baseId) === true) {
    return props.baseId;
  }

  return null;
}

function findCapabilityIdByBaseId(
  device: DeviceLightCapabilityDevice | null | undefined,
  baseId: LightCapabilityBaseId,
): string | null {
  for (const capabilityId of Object.keys(device?.capabilitiesObj ?? {})) {
    if (getCapabilityBaseId(capabilityId) === baseId) {
      return capabilityId;
    }
  }

  return null;
}

function getCapabilityBaseId(capabilityId: string | null | undefined): string | null {
  if (typeof capabilityId !== 'string') {
    return null;
  }

  return capabilityId.split('.')[0] ?? null;
}

function isLightCapabilityBaseId(baseId: string | null): baseId is LightCapabilityBaseId {
  if (baseId == null) {
    return false;
  }

  return LIGHT_CAPABILITY_BASE_IDS.some((lightCapabilityBaseId) => {
    return lightCapabilityBaseId === baseId;
  });
}

function getCapabilityInstanceSuffix(capabilityId: string | null): string | null {
  if (capabilityId == null) {
    return null;
  }

  const separatorIndex = capabilityId.indexOf('.');

  if (separatorIndex === -1) {
    return null;
  }

  return capabilityId.slice(separatorIndex + 1);
}

function hasCapability(
  device: DeviceLightCapabilityDevice | null | undefined,
  capabilityId: string | null | undefined,
): capabilityId is string {
  if (typeof capabilityId !== 'string') {
    return false;
  }

  return device?.capabilitiesObj?.[capabilityId] != null;
}

function isReadableBooleanCapability(
  device: DeviceLightCapabilityDevice | null | undefined,
  capabilityId: string | null | undefined,
): capabilityId is string {
  if (typeof capabilityId !== 'string') {
    return false;
  }

  const capability = device?.capabilitiesObj?.[capabilityId];

  return capability?.getable === true && capability.type === 'boolean';
}

function getResolvedValue(
  props: DeviceLightCapabilityValueProps,
  baseId: LightCapabilityBaseId,
): unknown {
  const capabilityId = props.capabilityIds[baseId];

  if (capabilityId == null) {
    return undefined;
  }

  return props.values[capabilityId];
}

function getNullableNumberValue(
  props: DeviceLightCapabilityValueProps,
  baseId: LightCapabilityBaseId,
): number | null | undefined {
  const value = getResolvedValue(props, baseId);

  if (value === null || typeof value === 'number') {
    return value;
  }

  return undefined;
}

function getNullableStringValue(
  props: DeviceLightCapabilityValueProps,
  baseId: LightCapabilityBaseId,
): string | null | undefined {
  const value = getResolvedValue(props, baseId);

  if (value === null || typeof value === 'string') {
    return value;
  }

  return undefined;
}

function getNullableBooleanValue(
  props: DeviceLightCapabilityValueProps,
  baseId: LightCapabilityBaseId,
): boolean | null | undefined {
  const value = getResolvedValue(props, baseId);

  if (value === null || typeof value === 'boolean') {
    return value;
  }

  return undefined;
}
